import React, { useState } from 'react';
import { Grid, Typography, Box, TextField, Button, IconButton, Paper } from '@mui/material';
import { MonitorPlay, FolderIcon, GripVertical } from 'lucide-react';
import { CloudUpload, Delete } from '@mui/icons-material';

const VideosMaterialsRow = ({
  videos,
  setVideos,
  documents,
  setDocuments,
  videoHandlers,
  documentHandlers,
  onSelectVideo,
  onUploadVideo,
  onEditVideo,
  onDeleteVideo,
  onSelectMaterial,
  onUploadMaterial,
  onEditMaterial,
  onDeleteMaterial,
  onReorderVideos,
  onReorderDocuments
}) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [totalVideoRows, setTotalVideoRows] = useState(4);
  const [totalDocumentRows, setTotalDocumentRows] = useState(4);

  // Helper function to check if a video has content
  const hasVideoContent = (video) => {
    return video && (video.name || video.file || video.selectedItem);
  };

  // Helper function to check if a document has content
  const hasDocumentContent = (doc) => {
    return doc && (doc.name || doc.file || doc.selectedItem);
  };

  // Get video rows with proper placeholder handling
  const getVideoRows = () => {
    const rows = [];
    for (let i = 0; i < totalVideoRows; i++) {
      if (i < videos.length) {
        rows.push(videos[i]);
      } else {
        rows.push({ name: '', file: null, selectedItem: null, source: null });
      }
    }
    return rows;
  };

  // Get document rows with proper placeholder handling
  const getDocumentRows = () => {
    const rows = [];
    for (let i = 0; i < totalDocumentRows; i++) {
      if (i < documents.length) {
        rows.push(documents[i]);
      } else {
        rows.push({ name: '', file: null, selectedItem: null, source: null });
      }
    }
    return rows;
  };

  // Handle adding more video rows
  const handleAddVideoRows = () => {
    setTotalVideoRows(prev => prev + 1);
  };

  // Handle adding more document rows
  const handleAddDocumentRows = () => {
    setTotalDocumentRows(prev => prev + 1);
  };

  // Improved drag and drop handlers for videos
  const handleVideoDragStart = (e, index) => {
    const video = videos[index];
    if (!hasVideoContent(video)) {
      e.preventDefault();
      return;
    }
    setDraggedItem({ type: 'video', index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Set a custom drag image for better UX
    const dragImage = e.target.cloneNode(true);
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleVideoDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleVideoDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem && draggedItem.type === 'video' && draggedItem.index !== index) {
      onReorderVideos(draggedItem.index, index);
    }
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleVideoDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Improved drag and drop handlers for documents
  const handleDocumentDragStart = (e, index) => {
    const doc = documents[index];
    if (!hasDocumentContent(doc)) {
      e.preventDefault();
      return;
    }
    setDraggedItem({ type: 'document', index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Set a custom drag image for better UX
    const dragImage = e.target.cloneNode(true);
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDocumentDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDocumentDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem && draggedItem.type === 'document' && draggedItem.index !== index) {
      onReorderDocuments(draggedItem.index, index);
    }
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDocumentDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const videoRows = getVideoRows();
  const documentRows = getDocumentRows();

  return (
    <Grid container spacing={4} sx={{ mt: 2, display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
      {/* Videos */}
      <Grid item xs={12} sm={6} sx={{ width: '100%', minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Contenido del curso
        </Typography>
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, background: '#fafbfc', p: 2 }}>
          {videoRows.map((video, idx) => {
            const hasContent = hasVideoContent(video);
            const isDragging = draggedItem?.type === 'video' && draggedItem.index === idx;
            const isDragOver = dragOverIndex === idx && draggedItem?.type === 'video';
            const isPlaceholder = idx >= videos.length;
            
            return (
              <Paper
                key={idx}
                draggable={hasContent}
                onDragStart={(e) => handleVideoDragStart(e, idx)}
                onDragOver={(e) => handleVideoDragOver(e, idx)}
                onDrop={(e) => handleVideoDrop(e, idx)}
                onDragEnd={handleVideoDragEnd}
                elevation={isDragging ? 8 : isDragOver ? 4 : 1}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  cursor: hasContent ? 'grab' : 'default',
                  opacity: isDragging ? 0.5 : isPlaceholder ? 0.7 : 1,
                  border: isDragOver ? '2px dashed #4DABA4' : '1px solid transparent',
                  background: isDragOver ? '#e6f7f5' : isPlaceholder ? '#f8f9fa' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: hasContent ? '#f5f5f5' : isPlaceholder ? '#f0f0f0' : 'transparent',
                    transform: hasContent ? 'translateY(-1px)' : 'none',
                  },
                  '&:active': {
                    cursor: hasContent ? 'grabbing' : 'default',
                  },
                  // Improved drag area - entire row is draggable
                  '&:hover .drag-handle': {
                    background: '#e6f7f5',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                {/* Improved Drag Handle - no preventDefault, better visual feedback */}
                {hasContent && (
                  <Box
                    className="drag-handle"
                    sx={{
                      mr: 1,
                      p: 0.5,
                      borderRadius: 1,
                      cursor: 'grab',
                      color: '#4DABA4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 32,
                      minHeight: 32,
                      transition: 'all 0.2s ease',
                      '&:active': {
                        cursor: 'grabbing',
                        transform: 'scale(0.95)'
                      }
                    }}
                  >
                    <GripVertical size={20} />
                  </Box>
                )}
                
                <Typography sx={{ minWidth: 70, fontWeight: 500, color: isPlaceholder ? '#999' : 'inherit' }}>
                  {`Video ${idx + 1}`}
                </Typography>
                {hasContent && (video.thumbnail || video.selectedItem?.thumbnail) && (
                  <Box
                    component="img"
                    src={video.thumbnail || video.selectedItem?.thumbnail}
                    alt={video.name || 'Miniatura'}
                    sx={{
                      width: 64,
                      height: 36,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mr: 1,
                      bgcolor: '#eee',
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                )}
                <TextField
                  value={video.name}
                  onChange={e => {
                    const newVideos = [...videos];
                    if (idx < videos.length) {
                      newVideos[idx] = { ...video, name: e.target.value };
                    } else {
                      // Add new video if it's a placeholder
                      newVideos[idx] = { name: e.target.value, file: null, selectedItem: null, source: null };
                    }
                    setVideos(newVideos);
                  }}
                  placeholder={isPlaceholder ? "Click para agregar video" : "Nombre"}
                  variant="standard"
                  sx={{ 
                    mx: 2, 
                    flex: 1, 
                    background: '#fff', 
                    borderRadius: 1,
                    '& .MuiInputBase-input': {
                      color: isPlaceholder ? '#999' : 'inherit'
                    }
                  }}
                  inputProps={{ maxLength: 40 }}
                />
                {hasContent && (video.duration || video.selectedItem?.duration) && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: '#555',
                      fontWeight: 600,
                      px: 1,
                      mr: 1,
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {video.duration || video.selectedItem?.duration}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 13, color: '#4DABA4', fontWeight: 600, px: 1, background: '#e6f7f5', borderRadius: 1, mr: 1 }}>
                  {video.source === 'existente' ? 'Existente' : video.source === 'nuevo' ? 'Nuevo' : ''}
                </Typography>
                <IconButton size="small" onClick={() => onSelectVideo(idx)}>
                  <MonitorPlay size={18} />
                </IconButton>
                <IconButton size="small" onClick={() => onUploadVideo(idx)}>
                  <CloudUpload fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => onDeleteVideo(idx)} 
                  disabled={!hasContent}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Paper>
            );
          })}
          <Button
            variant="outlined"
            fullWidth
            sx={{ mt: 1, fontWeight: 600, borderColor: '#4DABA4', color: '#4DABA4' }}
            onClick={handleAddVideoRows}
          >
            AGREGAR MÁS VIDEOS
          </Button>
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#888' }}>
            Mínimo 10 caracteres - Máximo 40
          </Typography>
        </Box>
      </Grid>
      
      {/* Materiales */}
      <Grid item xs={12} sm={6} sx={{ width: '100%', minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Material complementario
        </Typography>
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, background: '#fafbfc', p: 2 }}>
          {documentRows.map((doc, idx) => {
            const hasContent = hasDocumentContent(doc);
            const isDragging = draggedItem?.type === 'document' && draggedItem.index === idx;
            const isDragOver = dragOverIndex === idx && draggedItem?.type === 'document';
            const isPlaceholder = idx >= documents.length;
            
            return (
              <Paper
                key={idx}
                draggable={hasContent}
                onDragStart={(e) => handleDocumentDragStart(e, idx)}
                onDragOver={(e) => handleDocumentDragOver(e, idx)}
                onDrop={(e) => handleDocumentDrop(e, idx)}
                onDragEnd={handleDocumentDragEnd}
                elevation={isDragging ? 8 : isDragOver ? 4 : 1}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  cursor: hasContent ? 'grab' : 'default',
                  opacity: isDragging ? 0.5 : isPlaceholder ? 0.7 : 1,
                  border: isDragOver ? '2px dashed #4DABA4' : '1px solid transparent',
                  background: isDragOver ? '#e6f7f5' : isPlaceholder ? '#f8f9fa' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: hasContent ? '#f5f5f5' : isPlaceholder ? '#f0f0f0' : 'transparent',
                    transform: hasContent ? 'translateY(-1px)' : 'none',
                  },
                  '&:active': {
                    cursor: hasContent ? 'grabbing' : 'default',
                  },
                  // Improved drag area - entire row is draggable
                  '&:hover .drag-handle': {
                    background: '#e6f7f5',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                {/* Improved Drag Handle - no preventDefault, better visual feedback */}
                {hasContent && (
                  <Box
                    className="drag-handle"
                    sx={{
                      mr: 1,
                      p: 0.5,
                      borderRadius: 1,
                      cursor: 'grab',
                      color: '#4DABA4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 32,
                      minHeight: 32,
                      transition: 'all 0.2s ease',
                      '&:active': {
                        cursor: 'grabbing',
                        transform: 'scale(0.95)'
                      }
                    }}
                  >
                    <GripVertical size={20} />
                  </Box>
                )}
                
                <Typography sx={{ minWidth: 90, fontWeight: 500, color: isPlaceholder ? '#999' : 'inherit' }}>
                  {`Material ${idx + 1}`}
                </Typography>
                {hasContent && (doc.thumbnail || doc.selectedItem?.thumbnail) && (
                  <Box
                    component="img"
                    src={doc.thumbnail || doc.selectedItem?.thumbnail}
                    alt={doc.name || 'Portada'}
                    sx={{
                      width: 48,
                      height: 32,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mr: 1,
                      bgcolor: '#eee',
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                )}
                <TextField
                  value={doc.name}
                  onChange={e => {
                    const newDocs = [...documents];
                    if (idx < documents.length) {
                      newDocs[idx] = { ...doc, name: e.target.value };
                    } else {
                      // Add new document if it's a placeholder
                      newDocs[idx] = { name: e.target.value, file: null, selectedItem: null, source: null };
                    }
                    setDocuments(newDocs);
                  }}
                  placeholder={isPlaceholder ? "Click para agregar material" : "Nombre del material"}
                  variant="standard"
                  sx={{ 
                    mx: 2, 
                    flex: 1, 
                    background: '#fff', 
                    borderRadius: 1,
                    '& .MuiInputBase-input': {
                      color: isPlaceholder ? '#999' : 'inherit'
                    }
                  }}
                  inputProps={{ maxLength: 40 }}
                />
                {hasContent && (doc.contentType || doc.selectedItem?.contentType || doc.selectedItem?.type) && (
                  <Typography sx={{ fontSize: 11, color: '#555', fontWeight: 700, px: 1, mr: 0.5, textTransform: 'uppercase' }}>
                    {doc.contentType || doc.selectedItem?.contentType || doc.selectedItem?.type}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 13, color: '#4DABA4', fontWeight: 600, px: 1, background: '#e6f7f5', borderRadius: 1, mr: 1 }}>
                  {doc.source === 'existente' ? 'Existente' : doc.source === 'nuevo' ? 'Nuevo' : ''}
                </Typography>
                <IconButton size="small" onClick={() => onSelectMaterial(idx)}>
                  <FolderIcon size={18} />
                </IconButton>
                <IconButton size="small" onClick={() => onUploadMaterial(idx)}>
                  <CloudUpload fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => onDeleteMaterial(idx)} 
                  disabled={!hasContent}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Paper>
            );
          })}
          <Button
            variant="outlined"
            fullWidth
            sx={{ mt: 1, fontWeight: 600, borderColor: '#4DABA4', color: '#4DABA4' }}
            onClick={handleAddDocumentRows}
          >
            AGREGAR MÁS MATERIALES
          </Button>
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#888' }}>
            Mínimo 10 caracteres - Máximo 40
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default VideosMaterialsRow; 